import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import { ArrowLeft, FileText, Loader2, Upload } from "lucide-react";
import { adminApi, TOKEN_KEY, field, darkBtn } from "@/lib/adminApi";
import DocumentRow from "@/components/documents/DocumentRow";

const MAX = 50 * 1024 * 1024;

export default function AdminDocumentsPage() {
  const token = localStorage.getItem(TOKEN_KEY);
  const api = useMemo(() => adminApi(token), [token]);
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({ title: "", category: "", description: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const load = useCallback(() => api.get("/documents").then((r) => setDocs(r.data)).catch(() => toast.error("Chargement impossible.")), [api]);

  useEffect(() => {
    if (!token) window.location.href = "/admin";
    else load();
  }, [token, load]);

  if (!token) return null;

  const pick = (f) => {
    if (f && f.type !== "application/pdf") return toast.error("Choisissez un fichier PDF.");
    if (f && f.size > MAX) return toast.error("PDF trop volumineux (50 Mo max).");
    setFile(f || null);
  };

  const upload = async () => {
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    try {
      await api.post("/admin/documents", fd);
      toast.success("Document publié.");
      setForm({ title: "", category: form.category, description: "" });
      setFile(null);
      if (input.current) input.current.value = "";
      load();
    } catch (e) {
      if (e.response?.status === 401) window.location.href = "/admin";
      toast.error(e.response?.data?.detail || `Envoi impossible (${e.response?.status || "réseau"}).`);
    }
    setBusy(false);
  };

  const cats = [...new Set(docs.map((d) => d.category))];

  return (
    <div data-testid="admin-documents-page" className="min-h-screen bg-[#FAF8F5] px-4 pb-24 pt-8 text-stone-800">
      <div className="mx-auto max-w-4xl">
        <a data-testid="admin-documents-back" href="/admin" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-stone-500 transition-colors hover:text-[#C5A059]">
          <ArrowLeft className="h-3.5 w-3.5" /> Espace Famille
        </a>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <h1 className="flex items-center gap-3 font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
            <FileText className="h-7 w-7 text-[#C5A059]" /> Documents PDF
          </h1>
          <a href="/documents" data-testid="admin-documents-view-public" className="rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-600 transition-colors hover:border-[#C5A059]">
            Voir la page Documents
          </a>
        </div>

        <section data-testid="document-upload-form" className="mt-8 rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-semibold text-stone-900">Publier un document</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <input data-testid="document-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" maxLength={120} className={field} />
            <input data-testid="document-category-input" list="doc-cats" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Catégorie (ex. Biographie)" maxLength={60} className={field} />
            <datalist id="doc-cats">{[...new Set([...cats, "Biographie", "Hommages", "Programme des obsèques"])].map((c) => <option key={c} value={c} />)}</datalist>
            <textarea data-testid="document-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description courte (facultatif)" maxLength={300} rows={2} className={`${field} sm:col-span-2`} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button data-testid="document-file-button" onClick={() => input.current?.click()} className="inline-flex items-center gap-2 rounded-full border border-dashed border-stone-400 px-4 py-2.5 text-xs font-semibold text-stone-600 transition-colors hover:border-[#C5A059]">
              <Upload className="h-4 w-4" /> {file ? file.name : "Choisir un PDF (50 Mo max)"}
            </button>
            <input ref={input} data-testid="document-file-input" type="file" accept="application/pdf" className="hidden" onChange={(e) => pick(e.target.files[0])} />
            <button data-testid="document-upload-button" onClick={upload} disabled={busy || !file || !form.title.trim() || !form.category.trim()} className={`${darkBtn} ml-auto`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Publier
            </button>
          </div>
        </section>

        <section data-testid="document-list" className="mt-6 space-y-3">
          {docs.map((d) => <DocumentRow key={d.id} d={d} api={api} cats={cats} onChange={load} />)}
          {!docs.length && <p className="rounded-2xl border border-dashed border-stone-300 p-8 text-center text-xs text-stone-400">Aucun document publié.</p>}
        </section>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
